import { AssignmentGroupingType } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { DateTime } from 'luxon'
import { AssignmentType } from '@breakoutlearning/firebase-repository/types'
import type { useTranslationTyped } from 'i18n/i18n'
import { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { z } from 'zod'
import { dateSchema } from 'util/schema-date'
import { type Section } from '@breakoutlearning/firebase-repository/models/Section'

// solo (max group size 1): min must be 1 and join/leave must mirror creation
const validateSoloGroupSettings = (
  data: {
    groupingSize?: number
    groupingSizeMinimum?: number
    forbidStudentGroupCreation?: boolean
    forbidStudentGroupJoining?: boolean
    forbidStudentGroupLeaving?: boolean
  },
  ctx: z.RefinementCtx,
  tt: ReturnType<typeof useTranslationTyped>['tt']
) => {
  if (data.groupingSize !== 1) return
  if (
    data.groupingSizeMinimum !== undefined &&
    data.groupingSizeMinimum !== 1
  ) {
    ctx.addIssue({
      path: ['groupingSizeMinimum'],
      code: z.ZodIssueCode.custom,
      message: tt.instructor_library.minimum_exceeds_maximum(),
    })
  }
  const forbid = data.forbidStudentGroupCreation ?? false
  if (
    (data.forbidStudentGroupJoining ?? false) !== forbid ||
    (data.forbidStudentGroupLeaving ?? false) !== forbid
  ) {
    ctx.addIssue({
      // groupingSize renders its errors on the Grouping stage; the join
      // switch does not, which would make a blocked save invisible
      path: ['groupingSize'],
      code: z.ZodIssueCode.custom,
      message:
        tt.instructor_assignment.configure_assignment_dialog.solo_group_settings_invalid(),
    })
  }
}

export const getAssignmentSchema = ({
  tt,
  extendMinExpiresAtTo,
  sectionStartDate,
  sectionEndDate,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
  extendMinExpiresAtTo?: {
    date: DateTime
    message: string
  }
  sectionStartDate: Section['data']['sectionStartDate']
  sectionEndDate: Section['data']['sectionEndDate']
}) => {
  return z
    .object({
      assignmentType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentType)),
      sectionId: z
        .string({ message: tt.instructor_library.section_missing() })
        .min(1, tt.instructor_library.section_missing()),
      catalogId: z.string().optional(),
      assignedAt: dateSchema({
        required: tt.instructor_assignment.start_date_required(),
      }),
      dueDate: dateSchema({
        required: tt.instructor_library.deadline_required(),
      }),
      allowLateSubmissions: z.boolean().optional(),
      lateSubmissionCutoff: dateSchema({}).optional(),
      groupingType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentGroupingType)),
      groupingSize: z.coerce
        .number()
        // .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        // .max(RoomState.maxAllowedUsers, {
        //   message: tt.instructor_library.grouping_size_invalid(),
        // })
        .optional(),
      groupingSizeMinimum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMaximum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      configureGrading: z.boolean(),
      ltiGradingLineItemId: z.string().optional(),
      customGroupingSelected: z.boolean().optional(),
      forbidStudentGroupCreation: z.boolean().optional(),
      forbidStudentGroupScheduling: z.boolean().optional(),
      forbidStudentGroupSelection: z.boolean().optional(),
      forbidStudentGroupJoining: z.boolean().optional(),
      forbidStudentGroupLeaving: z.boolean().optional(),
      peerReviewsEnabled: z.boolean().optional(),
      peerReviewCriteria: z
        .array(
          z.object({
            prQuestion: z.string(),
            prDescription: z.string(),
          })
        )
        .optional(),
      peerReviewExcludesSelfReview: z.boolean().optional(),
      peerReviewsHiddenFromReviewees: z.boolean().optional(),
      showFinalGradeToParticipants: z.boolean().optional(),
      /** strictly used to inform validation, never written to assignment */
      roleGroupSizeConstraints: z
        .object({
          minGroupSize: z.number().int().min(1),
          maxGroupSize: z.number().int().max(RoomState.maxAllowedUsers),
        })
        .nullable()
        .optional(),
    })
    .superRefine((data, ctx) => {
      const {
        assignedAt,
        dueDate,
        peerReviewCriteria,
        allowLateSubmissions,
        lateSubmissionCutoff,
        peerReviewsEnabled,
        groupingSize,
        groupingSizeMinimum,
        roleGroupSizeConstraints,
      } = data

      if (roleGroupSizeConstraints) {
        const { minGroupSize, maxGroupSize } = roleGroupSizeConstraints
        let errMsg: string | undefined
        if (groupingSize !== undefined && groupingSize > maxGroupSize)
          errMsg = tt.instructor_library.grouping_size_beyond_role_max_error({
            maxGroupSize,
          })
        if (groupingSize !== undefined && groupingSize < minGroupSize)
          errMsg = tt.instructor_library.grouping_size_beyond_role_min_error({
            minGroupSize,
          })

        if (errMsg) {
          ctx.addIssue({
            path: ['groupingSize'],
            code: z.ZodIssueCode.custom,
            message: errMsg,
          })
        }

        if (
          groupingSizeMinimum !== undefined &&
          groupingSizeMinimum < minGroupSize
        ) {
          ctx.addIssue({
            path: ['groupingSizeMinimum'],
            code: z.ZodIssueCode.custom,
            message: tt.instructor_library.grouping_size_beyond_role_min_error({
              minGroupSize,
            }),
          })
        }
      } else {
        if (
          groupingSize !== undefined &&
          (groupingSize < 1 || groupingSize > RoomState.maxAllowedUsers)
        ) {
          ctx.addIssue({
            path: ['groupingSize'],
            code: z.ZodIssueCode.custom,
            message: tt.instructor_library.grouping_size_invalid(),
          })
        }
      }

      // Validate that groupingSizeMinimum <= groupingSize
      if (
        groupingSizeMinimum !== undefined &&
        groupingSize !== undefined &&
        groupingSizeMinimum > groupingSize
      ) {
        ctx.addIssue({
          path: ['groupingSizeMinimum'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_library.minimum_exceeds_maximum(),
        })
      }

      if (peerReviewsEnabled && groupingSize === 1) {
        ctx.addIssue({
          path: ['groupingSize'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one(),
        })
      }

      validateSoloGroupSettings(data, ctx, tt)

      if (assignedAt && dueDate && dueDate <= assignedAt) {
        return ctx.addIssue({
          path: ['dueDate'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.due_date_must_be_later(),
        })
      }

      if (allowLateSubmissions && lateSubmissionCutoff && dueDate) {
        if (lateSubmissionCutoff <= dueDate) {
          ctx.addIssue({
            path: ['lateSubmissionCutoff'],
            code: z.ZodIssueCode.custom,
            message:
              tt.instructor_library.submission_cutoff_must_be_after_due_date(),
          })
        }
      }

      if (allowLateSubmissions && !lateSubmissionCutoff) {
        ctx.addIssue({
          path: ['lateSubmissionCutoff'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_library.deadline_required(),
        })
      }

      if (
        sectionStartDate &&
        assignedAt < DateTime.fromJSDate(sectionStartDate).startOf('minute')
      )
        ctx.addIssue({
          path: ['assignedAt'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.assigned_at_before_section_start_date({
              date: DateTime.fromJSDate(sectionStartDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      const expiresAt =
        allowLateSubmissions && lateSubmissionCutoff
          ? lateSubmissionCutoff
          : dueDate

      if (sectionEndDate && expiresAt > DateTime.fromJSDate(sectionEndDate))
        ctx.addIssue({
          path: [allowLateSubmissions ? 'lateSubmissionCutoff' : 'dueDate'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.expiration_date_after_section_end_date({
              date: DateTime.fromJSDate(sectionEndDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      const rawMinExpiresAt = DateTime.now().minus({
        minutes: 1,
      })
      const minExpiresAt = extendMinExpiresAtTo
        ? DateTime.fromMillis(
            Math.min(
              extendMinExpiresAtTo.date.toMillis(),
              rawMinExpiresAt.toMillis()
            )
          )
        : rawMinExpiresAt

      if (expiresAt < minExpiresAt) {
        ctx.addIssue({
          path: [allowLateSubmissions ? 'lateSubmissionCutoff' : 'dueDate'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_library.expiration_date_must_be_future(),
        })
      }

      if (peerReviewCriteria) {
        validatePeerReviewCriteria(peerReviewCriteria, ctx)
      }
    })
}

export const getDraftAssignmentSchema = ({
  tt,
  sectionStartDate,
  sectionEndDate,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
  sectionStartDate: Section['data']['sectionStartDate']
  sectionEndDate: Section['data']['sectionEndDate']
}) => {
  return z
    .object({
      assignmentType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentType)),
      sectionId: z
        .string({ message: tt.instructor_library.section_missing() })
        .min(1, tt.instructor_library.section_missing()),
      catalogId: z.string().optional(),
      assignedAt: dateSchema({
        required: tt.instructor_assignment.start_date_required(),
      }).optional(),
      dueDate: dateSchema({
        required: tt.instructor_library.deadline_required(),
        min: {
          date: DateTime.now().minus({
            minutes: 1,
          }),
          message: tt.instructor_library.due_date_must_be_future(),
        },
      }).optional(),
      allowLateSubmissions: z.boolean().optional(),
      lateSubmissionCutoff: dateSchema({}).optional(),
      groupingType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentGroupingType)),
      groupingSize: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMinimum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMaximum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      configureGrading: z.boolean(),
      forbidStudentGroupCreation: z.boolean().optional(),
      forbidStudentGroupScheduling: z.boolean().optional(),
      forbidStudentGroupSelection: z.boolean().optional(),
      forbidStudentGroupJoining: z.boolean().optional(),
      forbidStudentGroupLeaving: z.boolean().optional(),
      peerReviewsEnabled: z.boolean().optional(),
      peerReviewCriteria: z
        .array(
          z.object({
            prQuestion: z.string(),
            prDescription: z.string(),
          })
        )
        .optional(),
      peerReviewExcludesSelfReview: z.boolean().optional(),
      peerReviewsHiddenFromReviewees: z.boolean().optional(),
      showFinalGradeToParticipants: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      const {
        assignedAt,
        dueDate,
        allowLateSubmissions,
        lateSubmissionCutoff,
        peerReviewCriteria,
        peerReviewsEnabled,
        groupingSize,
      } = data

      if (peerReviewsEnabled && groupingSize === 1) {
        ctx.addIssue({
          path: ['groupingSize'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one(),
        })
      }

      validateSoloGroupSettings(data, ctx, tt)

      if (assignedAt && dueDate && dueDate <= assignedAt) {
        return ctx.addIssue({
          path: ['dueDate'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_assignment.due_date_must_be_later(),
        })
      }

      if (allowLateSubmissions && lateSubmissionCutoff && dueDate) {
        if (lateSubmissionCutoff <= dueDate) {
          ctx.addIssue({
            path: ['lateSubmissionCutoff'],
            code: z.ZodIssueCode.custom,
            message:
              tt.instructor_library.submission_cutoff_must_be_after_due_date(),
          })
        }
      }

      if (allowLateSubmissions && !lateSubmissionCutoff) {
        ctx.addIssue({
          path: ['lateSubmissionCutoff'],
          code: z.ZodIssueCode.custom,
          message: tt.instructor_library.submission_cutoff_required(),
        })
      }

      if (
        sectionStartDate &&
        assignedAt &&
        assignedAt < DateTime.fromJSDate(sectionStartDate).startOf('minute')
      )
        ctx.addIssue({
          path: ['assignedAt'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.assigned_at_before_section_start_date({
              date: DateTime.fromJSDate(sectionStartDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      const expiresAt =
        allowLateSubmissions && lateSubmissionCutoff
          ? lateSubmissionCutoff
          : dueDate

      if (
        sectionEndDate &&
        expiresAt &&
        expiresAt > DateTime.fromJSDate(sectionEndDate)
      )
        ctx.addIssue({
          path: [allowLateSubmissions ? 'lateSubmissionCutoff' : 'dueDate'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.expiration_date_after_section_end_date({
              date: DateTime.fromJSDate(sectionEndDate).toLocaleString(
                DateTime.DATETIME_MED
              ),
            }),
        })

      if (peerReviewCriteria) {
        validatePeerReviewCriteria(peerReviewCriteria, ctx)
      }
    })
}

export const getLtiAssignmentSchema = ({
  tt,
}: {
  tt: ReturnType<typeof useTranslationTyped>['tt']
}) => {
  return z
    .object({
      catalogId: z.string().optional(),
      configureGrading: z.boolean(),
      customGroupingSelected: z.boolean().optional(),
      forbidStudentGroupCreation: z.boolean().optional(),
      forbidStudentGroupScheduling: z.boolean().optional(),
      forbidStudentGroupSelection: z.boolean().optional(),
      forbidStudentGroupJoining: z.boolean().optional(),
      forbidStudentGroupLeaving: z.boolean().optional(),
      peerReviewsEnabled: z.boolean().optional(),
      peerReviewCriteria: z
        .array(
          z.object({
            prQuestion: z.string().min(1, 'Criteria name is required').max(30),
            prDescription: z
              .string()
              .min(1, 'Criteria description is required')
              .max(180),
          })
        )
        .optional(),
      peerReviewExcludesSelfReview: z.boolean().optional(),
      peerReviewsHiddenFromReviewees: z.boolean().optional(),
      showFinalGradeToParticipants: z.boolean().optional(),
      groupingType: z.preprocess((val) => {
        return Number(val)
      }, z.nativeEnum(AssignmentGroupingType)),
      groupingSize: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        }),
      groupingSizeMinimum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
      groupingSizeMaximum: z.coerce
        .number()
        .min(1, { message: tt.instructor_library.grouping_size_invalid() })
        .max(RoomState.maxAllowedUsers, {
          message: tt.instructor_library.grouping_size_invalid(),
        })
        .optional(),
    })
    .superRefine((data, ctx) => {
      const { peerReviewCriteria, peerReviewsEnabled, groupingSize } = data

      if (peerReviewsEnabled && groupingSize === 1) {
        ctx.addIssue({
          path: ['groupingSize'],
          code: z.ZodIssueCode.custom,
          message:
            tt.instructor_assignment.configure_assignment_dialog.peer_review_disabled_max_group_size_one(),
        })
      }

      validateSoloGroupSettings(data, ctx, tt)

      if (peerReviewCriteria) {
        validatePeerReviewCriteria(peerReviewCriteria, ctx)
      }
    })
}

const validatePeerReviewCriteria = (
  peerReviewCriteria: Array<{ prQuestion: string; prDescription: string }>,
  ctx: z.RefinementCtx
) => {
  for (let i = 0; i < peerReviewCriteria.length; i++) {
    const criteria = peerReviewCriteria[i]
    if (!criteria.prQuestion || criteria.prQuestion.trim().length === 0) {
      ctx.addIssue({
        path: ['peerReviewCriteria'],
        code: z.ZodIssueCode.custom,
        message: `Errors with peer review criteria`,
      })
      ctx.addIssue({
        path: ['peerReviewCriteria', i, 'prQuestion'],
        code: z.ZodIssueCode.custom,
        message: `Criteria ${i + 1} name is required`,
      })
      break
    }
    if (criteria.prQuestion.length > 30) {
      ctx.addIssue({
        path: ['peerReviewCriteria'],
        code: z.ZodIssueCode.custom,
        message: `Errors with peer review criteria`,
      })
      ctx.addIssue({
        path: ['peerReviewCriteria', i, 'prQuestion'],
        code: z.ZodIssueCode.custom,
        message: `Criteria ${i + 1} name must be 30 characters or less`,
      })
      break
    }
    if (!criteria.prDescription || criteria.prDescription.trim().length === 0) {
      ctx.addIssue({
        path: ['peerReviewCriteria'],
        code: z.ZodIssueCode.custom,
        message: `Errors with peer review criteria`,
      })
      ctx.addIssue({
        path: ['peerReviewCriteria', i, 'prDescription'],
        code: z.ZodIssueCode.custom,
        message: `Criteria ${i + 1} description is required`,
      })
      break
    }
    if (criteria.prDescription.length > 180) {
      ctx.addIssue({
        path: ['peerReviewCriteria', i],
        code: z.ZodIssueCode.custom,
        message: `Errors with peer review criteria`,
      })
      ctx.addIssue({
        path: ['peerReviewCriteria', i, 'prDescription'],
        code: z.ZodIssueCode.custom,
        message: `Criteria ${i + 1} description must be 180 characters or less`,
      })
      break
    }
  }
}

export type AssignmentFormValues = z.infer<
  ReturnType<typeof getAssignmentSchema>
>
export type DraftAssignmentFormValues = z.infer<
  ReturnType<typeof getDraftAssignmentSchema>
>
export type LtiAssignmentFormValues = z.infer<
  ReturnType<typeof getLtiAssignmentSchema>
>
